import { hasAuthParams, type AuthContextProps } from "react-oidc-context";

// The page to come back to is passed through the sign-in in its state, and
// also kept here: a sign-in response that cannot be matched to its request
// (finished in another tab, e.g. after verifying an email, or stale) loses the
// state, and the callback URL itself (the site root) says nothing about it.
const RETURN_TO_KEY = "cppmodel.signIn.returnTo";
// When this tab last signed in again on its own, so that a sign-in that keeps
// failing, or a page that stays unauthorized after it, cannot loop.
const RETRIED_AT_KEY = "cppmodel.signIn.retriedAt";
const RETURN_TO_MAX_AGE_MS = 30 * 60 * 1000;
const RETRY_INTERVAL_MS = 60 * 1000;

// Only paths on this site: "//host" would leave it.
export const isSafeReturnPath = (path: unknown): path is string =>
  typeof path === "string" && path.startsWith("/") && !path.startsWith("//");

export const getCurrentPath = () => window.location.pathname + window.location.search + window.location.hash;

const rememberReturnTo = (path: string) => {
  try {
    localStorage.setItem(RETURN_TO_KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    // Storage blocked: the sign-in state still carries the path
  }
};

// The remembered page, once: it is forgotten as it is read.
export const takeReturnTo = (): string | null => {
  try {
    const stored = JSON.parse(localStorage.getItem(RETURN_TO_KEY) ?? "null");
    localStorage.removeItem(RETURN_TO_KEY);
    return stored && Date.now() - stored.at < RETURN_TO_MAX_AGE_MS && isSafeReturnPath(stored.path) ? stored.path : null;
  } catch {
    return null;
  }
};

// Whether this tab may sign in again on its own now; claims the attempt if so.
export const claimSignInRetry = (): boolean => {
  try {
    const retriedAt = Number(sessionStorage.getItem(RETRIED_AT_KEY));

    if (retriedAt && Date.now() - retriedAt < RETRY_INTERVAL_MS) {
      return false;
    }

    sessionStorage.setItem(RETRIED_AT_KEY, `${Date.now()}`);
    return true;
  } catch {
    return false;
  }
};

// Sign in and come back to returnTo: by default the current page, or, on the
// callback URL of a sign-in that failed, the page that sign-in was for.
export const signInAndReturn = (auth: AuthContextProps, returnTo?: string) => {
  const path = returnTo ?? (hasAuthParams() ? takeReturnTo() ?? "/" : getCurrentPath());

  rememberReturnTo(path);
  return auth.signinRedirect({ state: { returnTo: path } });
};
